import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getDependencies,
  createDependency,
  deleteDependency,
  RequirementDependency
} from '../services/requirementDependencyService';
import { RequirementWithDetails } from '../services/requirementService';

interface DependencyManagementProps {
  projectId: string;
  requirementId: string;
  requirementTitle: string;
  availableRequirements: RequirementWithDetails[];
  onDependencyChange?: () => void;
}

/**
 * DependencyManagement Component
 *
 * Manages workflow dependencies for a requirement.
 * Allows users to add and remove dependencies between requirements.
 */
const DependencyManagement: React.FC<DependencyManagementProps> = ({
  projectId,
  requirementId,
  requirementTitle,
  availableRequirements,
  onDependencyChange
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequirementId, setSelectedRequirementId] = useState('');
  const [dependencyType, setDependencyType] = useState<'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish'>('finish-to-start');
  const [isAdding, setIsAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch dependencies
  useEffect(() => {
    loadDependencies();
  }, [requirementId]);

  const loadDependencies = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getDependencies(projectId, requirementId, token);
      setDependencies(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dependencies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDependency = async () => {
    if (!token || !selectedRequirementId) return;

    try {
      setIsAdding(true);
      setError(null);
      await createDependency(
        projectId,
        requirementId,
        {
          depends_on_requirement_id: selectedRequirementId,
          dependency_type: dependencyType
        },
        token
      );

      // Reload dependencies
      await loadDependencies();

      // Reset form
      setSelectedRequirementId('');
      setDependencyType('finish-to-start');
      setShowAddForm(false);

      // Notify parent component
      if (onDependencyChange) {
        onDependencyChange();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add dependency');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteDependency = async (dependencyId: string) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to remove this dependency?')) return;

    try {
      setError(null);
      await deleteDependency(projectId, requirementId, dependencyId, token);

      // Reload dependencies
      await loadDependencies();

      // Notify parent component
      if (onDependencyChange) {
        onDependencyChange();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete dependency');
    }
  };

  // Get requirement title by ID
  const getRequirementTitle = (reqId: string): string => {
    const req = availableRequirements.find(r => r.id === reqId);
    return req ? req.title : 'Unknown Requirement';
  };

  // Filter available requirements (exclude current requirement and existing dependencies)
  const getAvailableRequirementsForDependency = () => {
    const existingDepIds = dependencies.map(d => d.depends_on_requirement_id);
    return availableRequirements.filter(
      req => req.id !== requirementId && !existingDepIds.includes(req.id)
    );
  };

  const formatDependencyType = (type: string): string => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Done
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-1.5 py-0.5 rounded">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded capitalize">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-500">
        Loading dependencies...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          Workflow Dependencies
        </h3>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-1 px-3 py-1 border border-transparent rounded-full text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Dependency
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200">
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      {/* Add Dependency Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 rounded-md border border-gray-200 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              This requirement depends on:
            </label>
            <select
              value={selectedRequirementId}
              onChange={(e) => setSelectedRequirementId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a requirement...</option>
              {getAvailableRequirementsForDependency().map(req => (
                <option key={req.id} value={req.id}>
                  {req.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Dependency Type:
            </label>
            <select
              value={dependencyType}
              onChange={(e) => setDependencyType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="finish-to-start">Finish to Start (FS)</option>
              <option value="start-to-start">Start to Start (SS)</option>
              <option value="finish-to-finish">Finish to Finish (FF)</option>
              <option value="start-to-finish">Start to Finish (SF)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Finish to Start: This requirement starts when the dependency finishes
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleAddDependency}
              disabled={!selectedRequirementId || isAdding}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-transparent rounded-full text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdding ? 'Adding...' : 'Add Dependency'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setSelectedRequirementId('');
                setError(null);
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dependencies List */}
      <div className="space-y-2">
        {dependencies.length === 0 ? (
          <p className="text-xs text-gray-500 italic">
            No workflow dependencies configured
          </p>
        ) : (
          dependencies.map((dep) => (
            <div
              key={dep.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-200 dark:border-gray-600"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {dep.predecessor_code && (
                    <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400">{dep.predecessor_code}</span>
                  )}
                  {dep.predecessor_status && getStatusBadge(dep.predecessor_status)}
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {dep.predecessor_title || getRequirementTitle(dep.depends_on_requirement_id)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {formatDependencyType(dep.dependency_type)}
                  {dep.lag_days !== 0 && ` (+${dep.lag_days} days lag)`}
                </p>
              </div>
              <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                <button
                  onClick={() => navigate(`/taskboard/requirement/${dep.depends_on_requirement_id}`)}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
                  title="Go to task"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteDependency(dep.id)}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info */}
      <p className="text-xs text-gray-500">
        Dependencies control the order requirements must be completed in the Gantt chart view.
      </p>
    </div>
  );
};

export default DependencyManagement;
